import { Pagination } from './api-response.model';

/**
 * Sentido de una opinión. Tras la entrega, el comprador opina sobre el
 * vendedor y el vendedor sobre el comprador: una por lado por cada pago.
 */
export type ReviewDirection = 'buyer_to_seller' | 'seller_to_buyer';

export const REVIEW_DIRECTIONS: ReviewDirection[] = ['buyer_to_seller', 'seller_to_buyer'];

export const REVIEW_DIRECTION_LABELS: Record<ReviewDirection, string> = {
  buyer_to_seller: 'Comprador → Vendedor',
  seller_to_buyer: 'Vendedor → Comprador',
};

/** Etiqueta en español de un sentido; tolera valores desconocidos del backend. */
export function reviewDirectionLabel(direction: string | null | undefined): string {
  return (direction && REVIEW_DIRECTION_LABELS[direction as ReviewDirection]) || '—';
}

/** Clases del distintivo de un sentido (`badge badge--…`), para pintarlo igual en todo el panel. */
export function reviewDirectionBadgeClass(direction: string | null | undefined): string {
  return direction === 'buyer_to_seller' ? 'badge badge--info' : 'badge badge--neutral';
}

/** Persona que opina o sobre la que se opina, tal como la manda el backend. */
export interface ReviewPerson {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  photo?: string | null;
  roleId?: number | null;
}

/**
 * Opinión (calificación en estrellas + comentario) ligada a un pago.
 *
 * `rater`/`rated` vienen anidados; los campos planos (`raterName`, `sellerUserId`…)
 * los agrega el endpoint de moderación. Los helpers `ratingRaterName()` y
 * compañía leen de donde haya dato.
 */
export interface Rating {
  id: number;
  paymentId?: number;
  direction?: ReviewDirection;
  /** 1–5 estrellas. */
  score?: number;
  comment?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Pieza de la compra, cuando el backend la resuelve. */
  partName?: string | null;
  offerId?: number | null;
  rater?: ReviewPerson | null;
  rated?: ReviewPerson | null;
  /* Campos planos del endpoint de moderación (`GET /ratings/admin`). */
  raterUserId?: number;
  raterName?: string;
  ratedUserId?: number;
  ratedName?: string;
  buyerUserId?: number;
  buyerName?: string;
  sellerUserId?: number;
  sellerName?: string;
  [key: string]: unknown;
}

/** Nombre presentable de una de las partes de una opinión. */
export function reviewPersonName(p: ReviewPerson | null | undefined): string {
  if (!p) return '';
  const full = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return p.name || full;
}

export function ratingRaterId(r: Rating): number | null {
  return r.rater?.id ?? r.raterUserId ?? null;
}

export function ratingRatedId(r: Rating): number | null {
  return r.rated?.id ?? r.ratedUserId ?? null;
}

export function ratingRaterName(r: Rating): string {
  const id = ratingRaterId(r);
  return r.raterName || reviewPersonName(r.rater) || (id != null ? `Usuario #${id}` : '—');
}

export function ratingRatedName(r: Rating): string {
  const id = ratingRatedId(r);
  return r.ratedName || reviewPersonName(r.rated) || (id != null ? `Usuario #${id}` : '—');
}

/** Conteo de opiniones por número de estrellas. */
export type RatingDistribution = Record<'1' | '2' | '3' | '4' | '5', number>;

export function emptyRatingDistribution(): RatingDistribution {
  return { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
}

/** Promedio, total y reparto por estrellas de un conjunto de opiniones. */
export interface RatingSummary {
  average: number | null;
  count: number;
  distribution: RatingDistribution;
}

export function emptyRatingSummary(): RatingSummary {
  return { average: null, count: 0, distribution: emptyRatingDistribution() };
}

/** Normaliza el bloque de resumen que mandan los endpoints de opiniones. */
export function toRatingSummary(raw: Record<string, unknown> | null | undefined): RatingSummary {
  const summary = emptyRatingSummary();
  if (!raw) return summary;
  const average = Number(raw['average']);
  summary.average = raw['average'] == null || !Number.isFinite(average) ? null : average;
  summary.count = Number(raw['count']) || 0;
  const dist = raw['distribution'] as Record<string, unknown> | undefined;
  if (dist && typeof dist === 'object') {
    for (const key of Object.keys(summary.distribution) as (keyof RatingDistribution)[]) {
      summary.distribution[key] = Number(dist[key]) || 0;
    }
  }
  return summary;
}

/** Ficha del usuario que encabeza `GET /ratings?userId=`. */
export interface ReviewedUser extends ReviewPerson {
  /** Calificación efectiva (promedio o ajuste manual). */
  rating?: number | null;
  ratingCount?: number;
  memberSince?: string | null;
}

/** Opiniones recibidas por un usuario, con su ficha y resumen. */
export interface UserReviews {
  user: ReviewedUser | null;
  items: Rating[];
  summary: RatingSummary;
  pagination?: Pagination;
}

/** Resultado de fijar o restablecer la calificación de un usuario (panel admin). */
export interface UserRatingResult {
  userId: number;
  rating: number;
  ratingOverride: number | null;
  /** `true` cuando el valor quedó fijado a mano. */
  isManual: boolean;
}

/** Límites y paso del ajuste manual de calificaciones. */
export const RATING_MIN = 0;
export const RATING_MAX = 5;
export const RATING_STEP = 0.5;

/** Acota un valor a 0–5 y lo redondea al paso de media estrella. */
export function clampRating(value: number): number {
  const snapped = Math.round(value / RATING_STEP) * RATING_STEP;
  return Math.min(RATING_MAX, Math.max(RATING_MIN, Number(snapped.toFixed(2))));
}
